import React, { useCallback, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import {
  EditableLegalClientSchema,
  IEditableLegalClient,
} from '../../../../../Interfaces/IEditLegalClient';
import {
  getEmptyLegalClient,
  ILegalClient,
} from '../../../../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  getEmptyParticipantMember,
  isParticipantMember,
} from '../../../../../Interfaces/ILegalTransactionRelationship/Members/IMemberParticipant';
import { getEmptyNaturalPerson } from '../../../../../Interfaces/INaturalPerson';
import { DialogResponsive } from '../../../../core/DialogResponsive';
import { EditLegalClient } from '../../../../LegalClient/EditLegalClient';
import { TableLegalClient } from '../../../../LegalClient/TableLegalClient';
import { useLegalClientStore } from '../../generic/legalClientStore';
import { NavRow } from '../../generic/NavRow';
import {
  removeLegalClientFromRelationships,
  updateOrCreateMember,
  updateOrCreateRelationship,
} from '../../generic/utilities';
import { getOrCreateGeneralRelationship } from '../utilities';

interface IGeneralEditLegalClientsProps {
  relationshipArray: ILegalTransactionRelationship[];
  setRelationshipArray: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
  handleNext: () => void;
  handleBack: () => void;
}

export const GeneralEditLegalClients: React.FC<
  IGeneralEditLegalClientsProps
> = ({ relationshipArray, setRelationshipArray, handleNext, handleBack }) => {
  const [legalClientArray, putLegalClient, removeLegalClient] =
    useLegalClientStore(state => [
      state.legalClientArray,
      state.putLegalClient,
      state.removeLegalClient,
    ]);

  const methods = useForm<IEditableLegalClient>({
    mode: 'onChange',
    defaultValues: {
      ...getEmptyLegalClient(),
      ...getEmptyNaturalPerson(),
    },
    resolver: zodResolver(EditableLegalClientSchema),
  });

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenEdit, setIsOpenEdit] = useState(false);

  const generalRelationship = useMemo(
    () => getOrCreateGeneralRelationship(relationshipArray),
    [relationshipArray]
  );
  const members = useMemo(
    () => generalRelationship.Members.filter(isParticipantMember),
    [generalRelationship]
  );
  const memberLegalClients = useMemo(
    () =>
      legalClientArray.filter(legalClient =>
        members.some(
          member => member.idLegalClient === legalClient.idLegalClient
        )
      ),
    [members, legalClientArray]
  );

  const removeLegalClientFromStoreAndRelationships = useCallback(
    (legalClientToRemove: ILegalClient) => {
      removeLegalClient(legalClientToRemove.idLegalClient);

      const updatedRelationships = removeLegalClientFromRelationships(
        relationshipArray,
        legalClientToRemove.idLegalClient
      );
      setRelationshipArray(updatedRelationships);
    },
    [members, generalRelationship, relationshipArray, setRelationshipArray]
  );

  const isOpen = isOpenAdd || isOpenEdit;

  const onClickNew = () => {
    methods.reset({
      ...getEmptyLegalClient(legalClientArray),
      ...getEmptyNaturalPerson(),
    });
    setIsOpenAdd(true);
  };

  const onClickEdit = (legalClient: ILegalClient) => {
    const existingMember = members.find(
      member => member.idLegalClient === legalClient.idLegalClient
    );

    if (!existingMember || !legalClient) {
      console.error(
        `No member or legal client found in onClickEdit for legal client with ID: ${legalClient.idLegalClient}`
      );
      return;
    }

    methods.reset({ ...legalClient } as IEditableLegalClient);
    setIsOpenEdit(true);
  };

  const onClose = () => {
    setIsOpenAdd(false);
    setIsOpenEdit(false);
  };

  const onSave = (data: ILegalClient) => {
    putLegalClient(data);

    const relationship = getOrCreateGeneralRelationship(relationshipArray);

    const newMember = getEmptyParticipantMember(
      data,
      relationship.idRelationship
    );

    setRelationshipArray(
      updateOrCreateRelationship(relationshipArray, {
        ...relationship,
        Members: updateOrCreateMember(relationship.Members, newMember),
      })
    );
    onClose();
  };

  const onSubmit = methods.handleSubmit(data => {
    onSave(data as ILegalClient);
  });

  const { isValid } = methods.formState;

  console.log(legalClientArray, relationshipArray);

  return (
    <Box>
      <DialogResponsive
        open={isOpen}
        onClose={onClose}
        title={isOpenAdd ? 'Neue Person' : 'Person bearbeiten'}
        maxWidth='lg'
      >
        <FormProvider {...methods}>
          <form id='edit-legal-client-form' onSubmit={onSubmit} noValidate>
            <EditLegalClient disableCompanyLegalClient />
            <NavRow
              justify='right'
              handleBack={onClose}
              backButtonText='Abbrechen'
              nextButtonText='Übernehmen'
              disableNext={!isValid}
            />
          </form>
        </FormProvider>
      </DialogResponsive>

      <TableLegalClient
        legalClientArray={memberLegalClients}
        onRemoveClick={removeLegalClientFromStoreAndRelationships}
        onClickNew={onClickNew}
        onRowClick={onClickEdit}
        title='Beteiligte Personen'
      />

      <NavRow
        handleBack={handleBack}
        handleNext={handleNext}
        disableNext={legalClientArray.length === 0}
      />
    </Box>
  );
};
